import type React from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';

interface ConfirmModalProps {
	message: string;
	selectedData: string;
	modalActive: boolean;
	triggerModal(): void;
	onConfirm(): void;
	onCancel(): void;
	children?: React.ReactNode;
	validated?: boolean;
}

const ConfirmModal = ({
	message,
	selectedData,
	modalActive,
	triggerModal,
	onConfirm,
	onCancel,
	children,
	validated = true,
}: ConfirmModalProps) => {
	const [isConfirming, setIsConfirming] = useState(false);
	const isMobile = useIsBreakpoint('max', 768);

	const handleConfirm = async () => {
		setIsConfirming(true);
		await onConfirm();
		triggerModal();
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) triggerModal();
	};

	const actions = (
		<div className="flex gap-3">
			<Button
				autoFocus
				isLoading={isConfirming}
				disabled={isConfirming || !validated}
				onClick={handleConfirm}
				type="button"
				variant="default"
				size="small"
				className="w-[75px]"
			>
				Yes
			</Button>
			<Button
				disabled={isConfirming || !validated}
				onClick={onCancel}
				type="button"
				variant="secondary"
				size="small"
				className="w-[75px]"
			>
				Cancel
			</Button>
		</div>
	);

	const body = (
		<div className="space-y-2">
			<p className="text-base text-purple-900">{message}</p>
			<p className="text-base font-semibold text-purple-900">{selectedData}</p>
			{children}
		</div>
	);

	if (isMobile) {
		return (
			<Drawer open={modalActive} onOpenChange={handleOpenChange}>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle>Confirm Action</DrawerTitle>
						<DrawerDescription>Review the details below before confirming.</DrawerDescription>
					</DrawerHeader>
					<div className="px-4 pb-2 flex-1 overflow-y-auto">{body}</div>
					<DrawerFooter>{actions}</DrawerFooter>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={modalActive} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="pb-2">Confirm Action</DialogTitle>
					<DialogDescription className="text-sm text-muted-foreground">
						Review the details below before confirming.
					</DialogDescription>
				</DialogHeader>
				<div className="mb-4">{body}</div>
				{actions}
			</DialogContent>
		</Dialog>
	);
};

export default ConfirmModal;
